import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { THEATERS, THEATER_CHAINS, THEATER_CHAIN_ORGS, THEATER_LOCATIONS, THEATER_WEBSITES, THEATER_ADDRESSES, THEATER_ABOUT, getTicketLink } from './constants';
import { supabaseShowTimes, supabaseMovies } from './supabase';
import { VEEZI_PREVIEW, VEEZI_PREVIEW_CINEMA, loadVeeziPreview } from './utils/veeziPreview';
import { formatMovieTitle } from './utils/titleFormatting';
import { slugify } from './utils/slugify';
import { posterSrc } from './utils/poster';
import { isExclusionActive } from './utils/moderationHelpers';
import './MovieDetail.css';


function TheaterDetail() {
  const { theaterSlug } = useParams();
  const [movies, setMovies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Find theater info by slug
  const theater = THEATERS.find(t => t.slug === theaterSlug);
  const theaterId = theater?.id;
  const theaterLocation = theaterId ? THEATER_LOCATIONS[theaterId] : null;

  useEffect(() => {
    async function fetchTheaterMovies() {
      if (!theater) {
        setIsLoading(false);
        return;
      }

      try {
        // Get all showtimes for this theater (next 90 days)
        // Use start of today so films already playing today show "Now Playing"
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const futureDate = new Date(now.getTime() + (90 * 24 * 60 * 60 * 1000));
        const timeFrom = startOfToday.toISOString();
        const timeTo = futureDate.toISOString();

        let showtimes = await supabaseShowTimes.getAllWithinTimeFrameByTheater(
          theaterId,
          timeFrom,
          timeTo
        );

        // Local-dev preview: serve this theater purely from the static export
        // (see utils/veeziPreview) — no Supabase, no IST fallback.
        let previewMovies = null;
        if (VEEZI_PREVIEW && theaterId === VEEZI_PREVIEW_CINEMA) {
          const p = await loadVeeziPreview();
          if (p) { showtimes = p.showtimes || []; previewMovies = p.movies || {}; }
        }

        // (IST fallback removed 2026-07-13 — it displayed stale IST junk whenever a
        // venue's DB result came back empty. Data ownership is server-side only; an
        // empty venue renders empty until its owner's next cron.)

        if (!showtimes || showtimes.length === 0) {
          setMovies([]);
          setIsLoading(false);
          return;
        }

        // Get unique movie IDs
        const movieIds = [...new Set(showtimes.map(st => st.movie_id))];

        // Fetch movie details
        const movieDetails = await supabaseMovies.getByIds(movieIds);
        const movieDetailsMap = {};
        if (movieDetails) {
          movieDetails.forEach(movie => {
            movieDetailsMap[movie.id] = movie;
          });
        }
        // Overlay preview movie records (enriched placeholders / healed matches
        // that aren't in Supabase yet) so the theater page renders them.
        if (previewMovies) {
          for (const [id, m] of Object.entries(previewMovies)) {
            movieDetailsMap[id] = { ...(movieDetailsMap[id] || {}), ...m };
          }
        }

        // Group showtimes by movie
        // Match CalendarView: cutoff is Jan 1 of last year (= Jan 1, 2025)
        const revivalCutoff = new Date(new Date().getFullYear() - 1, 0, 1);

        const isChainTheater = THEATER_CHAINS.some(c => c.ids.includes(theaterId));

        const movieGroups = {};
        showtimes.forEach(showtime => {
          const movieData = movieDetailsMap[showtime.movie_id];

          // Skip excluded films (parse guarded — a malformed record must not
          // blank the whole theater page; 2026-07-21 hardening pass)
          const moderationInfo = (() => {
            const mi = movieData?.moderation_info;
            if (!mi) return null;
            if (typeof mi !== 'string') return mi;
            try { return JSON.parse(mi); } catch { return null; }
          })();
          if (isExclusionActive(moderationInfo)) return;

          // Skip movies not in the curated revival DB — applies to all theaters
          if (!movieData) return;

          // Skip films from this year or last year — applies to ALL theaters
          const dates = movieData.release_dates
            ? Object.values(movieData.release_dates).flat().map(r => r.date).filter(Boolean).sort()
            : [];
          if (dates.length === 0 || new Date(dates[0]) >= revivalCutoff) return;

          if (!movieGroups[showtime.movie_id]) {
            movieGroups[showtime.movie_id] = {
              movie_id: showtime.movie_id,
              title: formatMovieTitle(showtime.cinema_movie_title, theaterId),
              release_dates: movieData?.release_dates,
              // Carried so the render's title cascade can actually fire — a foreign
              // customTitle ("Original (English) (Year)") and the release_year year
              // fallback were previously undefined here (dead code).
              moderation_info: moderationInfo,
              release_year: movieData?.release_year || null,
              slug: movieData?.slug || null,
              poster_path: movieData?.poster_path || null,
              showtimes: []
            };
          }
          movieGroups[showtime.movie_id].showtimes.push(showtime);
        });

        // Convert to array and sort by earliest release date
        const getEarliestDate = (movie) => {
          if (!movie.release_dates) return Infinity;
          const dates = Object.values(movie.release_dates).flat().map(r => r.date).filter(Boolean).sort();
          return dates.length > 0 ? new Date(dates[0]).getTime() : Infinity;
        };
        const moviesArray = Object.values(movieGroups).sort((a, b) => getEarliestDate(a) - getEarliestDate(b));

        setMovies(moviesArray);
      } catch (error) {
        console.error('Error fetching theater movies:', error);
        setMovies([]);
      } finally {
        setIsLoading(false);
      }
    }

    fetchTheaterMovies();
  }, [theaterSlug, theater, theaterId]);

  if (!theater) {
    return (
      <div className="movie-detail">
        <h2>Theater not found</h2>
        <Link to="/">← Return to homepage</Link>
      </div>
    );
  }

  const metaTitle = `${theater.name} - Revival & Repertory Films | Film Revival NYC`;
  const metaDescription = `Find every revival and repertory film currently playing at ${theater.name} in ${theaterLocation || 'New York'}. View showtimes and buy tickets.`;

  const theaterChain = THEATER_CHAINS.find(c => c.ids.includes(theaterId));
  const parentOrg = theaterChain ? THEATER_CHAIN_ORGS[theaterChain.name] : null;
  const theaterWebsite = THEATER_WEBSITES[theaterId] || (parentOrg ? parentOrg.url : null);
  const theaterAddress = THEATER_ADDRESSES[theaterId] || null;
  const theaterAbout = THEATER_ABOUT[theaterId] || null;

  const theaterSchema = {
    "@context": "https://schema.org",
    "@type": "MovieTheater",
    "name": theater.name,
    "url": theaterWebsite || `https://filmrevivalnyc.com/theater/${theater.slug}`,
    "address": {
      "@type": "PostalAddress",
      ...(theaterAddress && { "streetAddress": theaterAddress.split(',')[0] }),
      "addressLocality": theaterLocation || "New York",
      "addressRegion": "NY",
      "addressCountry": "US"
    },
    "description": metaDescription,
    ...(parentOrg && {
      "parentOrganization": {
        "@type": "Organization",
        "name": parentOrg.name,
        "url": parentOrg.url
      }
    })
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://filmrevivalnyc.com/theater/${theater.slug}`} />
        <meta property="og:image" content="https://filmrevivalnyc.com/FILM_REVIVAL_NY_OG.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content="https://filmrevivalnyc.com/FILM_REVIVAL_NY_OG.jpg" />
        <link rel="canonical" href={`https://filmrevivalnyc.com/theater/${theater.slug}`} />
        <script type="application/ld+json">{JSON.stringify(theaterSchema)}</script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://filmrevivalnyc.com/" },
              { "@type": "ListItem", "position": 2, "name": theater.name, "item": `https://filmrevivalnyc.com/theater/${theater.slug}` }
            ]
          })}
        </script>
      </Helmet>

      <div className="movie-detail">
        <div style={{ 
          textAlign: 'left', 
          marginBottom: '20px',
          position: 'relative'
        }}>
          <Link to="/" style={{
            textDecoration: 'none',
            color: '#d4a843',
            fontSize: '14px',
            display: 'inline-block'
          }}>
            ← return to homepage
          </Link>
        </div>

        <h2>{theater.name}</h2>
        {theaterLocation && (
          <p style={{ fontSize: '1.1rem', color: '#8a8272', marginBottom: '8px' }}>
            {theaterLocation}
          </p>
        )}
        {theaterAddress && (
          <p style={{ fontSize: '0.9rem', color: '#8a8272', marginBottom: '8px' }}>
            <a
              href={`https://maps.google.com/?q=${encodeURIComponent(theaterAddress)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#8a8272', textDecoration: 'none' }}
            >
              📍 {theaterAddress}
            </a>
          </p>
        )}
        {theaterWebsite && (
          <p style={{ marginBottom: '12px' }}>
            <a
              href={theaterWebsite}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#d4a843', fontSize: '0.95rem' }}
            >
              Visit {parentOrg ? parentOrg.name : 'theater website'} →
            </a>
          </p>
        )}
        {theaterAbout && (
          <p style={{ fontSize: '0.95rem', color: '#c0b8a8', maxWidth: '600px', lineHeight: '1.6', margin: '0 auto 24px auto', textAlign: 'center' }}>
            {theaterAbout}
          </p>
        )}

        {isLoading ? (
          <p>Loading movies...</p>
        ) : movies.length === 0 ? (
          <p>No upcoming revival or repertory films at this theater.</p>
        ) : (
          <>
            <h3>Current Revival & Repertory Films ({movies.length})</h3>
            <div style={{ marginTop: '30px' }}>
              {movies.map(movie => {
                // Match the calendar's display: custom title (e.g. foreign
                // "Original (English) (Year)") if set; year appended exactly once
                // via the same cascade (customTitle → release_dates → release_year).
                const custom = movie.moderation_info?.customTitle || null;
                const cleanTitle = (custom || movie.title || '').replace(/\s*\(\d{4}\)/g, '').trim();
                let releaseYear = null;
                if (custom) { const m = custom.match(/\((\d{4})\)/); if (m) releaseYear = parseInt(m[1]); }
                if (!releaseYear && movie.release_dates && Object.keys(movie.release_dates).length) {
                  const d = Object.values(movie.release_dates).flat().map(r => r && r.date).filter(Boolean).sort()[0];
                  if (d) releaseYear = new Date(d).getFullYear();
                }
                if (!releaseYear && movie.release_year) releaseYear = parseInt(movie.release_year);
                const displayTitle = releaseYear ? `${cleanTitle} (${releaseYear})` : cleanTitle;
                const filmSlug = movie.slug || slugify((movie.title || '').replace(/\s*\(\d{4}\)/g, '').trim(), releaseYear);
                const filmUrl = filmSlug ? `/films/${filmSlug}` : `/movie/${movie.movie_id}`;

                // Get earliest showtime date
                const sortedShowtimes = movie.showtimes
                  .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
                const earliestShowtime = sortedShowtimes[0];
                const earliestDateTime = earliestShowtime ? new Date(earliestShowtime.start_at) : null;
                const todayDate = new Date();
                const threeDaysFromNow = new Date(todayDate.getTime() + 3 * 24 * 60 * 60 * 1000);
                const isPlayingNow = earliestDateTime && earliestDateTime <= threeDaysFromNow;
                const earliestDate = earliestDateTime
                  ? earliestDateTime.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                  : '';
                const playingLabel = isPlayingNow ? 'Now Playing' : (earliestDate ? `Opens ${earliestDate}` : '');

                // Check for film format
                const hasFilmFormat = movie.showtimes.some(st => 
                  st.film_format === '35mm' || st.film_format === '70mm'
                );

                // Get ticket link
                const ticketLink = getTicketLink(earliestShowtime?.booking_link, theaterId);

                return (
                  <div key={movie.movie_id} style={{
                    marginBottom: '25px',
                    paddingBottom: '20px',
                    borderBottom: '1px solid #2a2520'
                  }}>
                    {posterSrc(movie.poster_path) && (
                      <Link to={filmUrl} style={{ display: 'block', marginBottom: '8px' }}>
                        <img
                          src={posterSrc(movie.poster_path)}
                          alt={displayTitle}
                          style={{
                            width: '220px',
                            height: '124px',
                            objectFit: 'cover',
                            borderRadius: '6px'
                          }}
                          loading="lazy"
                        />
                      </Link>
                    )}
                    <h4 style={{ marginBottom: '8px' }}>
                      <Link
                        to={filmUrl}
                        style={{ textDecoration: 'none', color: '#d4a843' }}
                      >
                        {displayTitle} {hasFilmFormat && '🎞️'}
                      </Link>
                    </h4>
                    {playingLabel && (
                      <p style={{ fontSize: '0.9rem', color: '#8a8272', marginBottom: '10px' }}>
                        {playingLabel}
                      </p>
                    )}
                    <div>
                      <Link
                        to={filmUrl}
                        style={{
                          display: 'inline-block',
                          padding: '8px 16px',
                          backgroundColor: '#d4a843',
                          color: '#0d0d0d',
                          textDecoration: 'none',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          marginRight: '10px'
                        }}
                      >
                        Film Info & Showtimes
                      </Link>
                      {ticketLink && (
                        <a
                          href={ticketLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-block',
                            padding: '8px 16px',
                            backgroundColor: 'transparent',
                            color: '#d4a843',
                            textDecoration: 'none',
                            borderRadius: '4px',
                            fontSize: '0.9rem',
                            border: '1px solid #d4a843'
                          }}
                        >
                          Buy Tickets
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default TheaterDetail;

